import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import BoardPackExecutiveSummary from './BoardPackExecutiveSummary';
import BoardScreen from './BoardScreen';
import closeIntelApi from './closeIntelApi';
import SectionGuide from './SectionGuide';
import { SCREEN_DEFS, basisTabLabel, screenById, screenForGlGroup } from './boardScreenHelpers';

export const ROLLUP_ORDER = [
  'Interest Income',
  'Interest Expense',
  'Non-Interest Income',
  'Non-Interest Expense',
  'Provision',
  'Tax',
  'Balance Sheet'
];

const BASIS_OPTIONS = ['budget', 'prior', 'prior_period', 'prior_year'];

export const canonicalRollup = (rollup) => {
  const value = String(rollup || 'Balance Sheet');
  if (/non[- ]interest income/i.test(value)) return 'Non-Interest Income';
  if (/non[- ]interest expense/i.test(value)) return 'Non-Interest Expense';
  if (/interest income/i.test(value)) return 'Interest Income';
  if (/interest expense/i.test(value)) return 'Interest Expense';
  if (/provision/i.test(value)) return 'Provision';
  if (/tax/i.test(value)) return 'Tax';
  if (/balance/i.test(value)) return 'Balance Sheet';
  return value;
};

const rollupRank = (rollup) => {
  const canonical = canonicalRollup(rollup);
  const index = ROLLUP_ORDER.indexOf(canonical);
  return index === -1 ? ROLLUP_ORDER.length : index;
};

export const groupBoardLines = (lines = []) => {
  const grouped = new Map();
  lines.forEach((line) => {
    const rollup = canonicalRollup(line.rollup);
    if (!grouped.has(rollup)) grouped.set(rollup, []);
    grouped.get(rollup).push(line);
  });
  return Array.from(grouped.entries())
    .map(([rollup, rows]) => ({
      rollup,
      rows: [...rows].sort((a, b) => String(a.glGroup || '').localeCompare(String(b.glGroup || '')))
    }))
    .sort((a, b) => {
      const rankDelta = rollupRank(a.rollup) - rollupRank(b.rollup);
      return rankDelta || a.rollup.localeCompare(b.rollup);
    });
};

const runId = (run = {}) => run.id || run.runId || run.closeRunId || run.close_run_id;

const boardRunStatusRank = (run = {}) => {
  const status = String(run.status || '').toLowerCase();
  return status === 'running' || status === 'completed' ? 0 : 1;
};

export const orderBoardRuns = (runs = []) => [...runs]
  .filter((run) => run.kind === 'close')
  .sort((a, b) => {
    const statusDelta = boardRunStatusRank(a) - boardRunStatusRank(b);
    if (statusDelta) return statusDelta;
    return String(b.startedAt || b.started_at || '').localeCompare(
      String(a.startedAt || a.started_at || '')
    );
  });

const bridgeKey = (line = {}) =>
  `${line.scopeLevel || 'line'}-${line.planningEntity || ''}-${line.glGroup || ''}`;

const findingLookup = (findings = []) => {
  const lookup = {};
  findings.forEach((finding) => {
    lookup[finding.id] = finding;
  });
  return lookup;
};

const readParam = (name) => {
  if (typeof window === 'undefined') return '';
  return new URLSearchParams(window.location.search).get(name) || '';
};

const writeParam = (name, value) => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (value) url.searchParams.set(name, value);
  else url.searchParams.delete(name);
  window.history.replaceState({}, '', url);
};

export const executiveDetailTarget = (screenId, executiveItemId = '') => ({
  screenId: screenById(screenId).id,
  executiveItemId,
  returnToExecutive: true
});

export const executiveReturnTarget = () => ({
  screenId: 'exec',
  returnToExecutive: false
});

export default function BoardPage({ navigateTab }) {
  const [runs, setRuns] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState(() => readParam('run'));
  const [basis, setBasis] = useState('budget');
  const [screenId, setScreenId] = useState(() => screenById(readParam('screen')).id);
  const [executiveItemId, setExecutiveItemId] = useState(() => readParam('execItem'));
  const [returnToExecutive, setReturnToExecutive] = useState(
    () => readParam('returnTo') === 'exec'
  );
  const [board, setBoard] = useState(null);
  const [bridges, setBridges] = useState({});
  const [loadingBridgeKeys, setLoadingBridgeKeys] = useState(new Set());
  // Refs mirror bridge fetch state so fetchBridge can decide synchronously
  // (deciding inside nested setState updaters is not reliable in production React).
  const bridgesRef = useRef({});
  const inFlightRef = useRef(new Set());
  const [findings, setFindings] = useState([]);
  const [findingsLoading, setFindingsLoading] = useState(false);
  const [expectationsByKey, setExpectationsByKey] = useState({});
  const [evidence, setEvidence] = useState({});
  const [focusLineKey, setFocusLineKey] = useState('');
  const [error, setError] = useState('');

  const loadRuns = async () => {
    setError('');
    try {
      const payload = await closeIntelApi.listRuns();
      const closeRuns = orderBoardRuns(payload?.runs || []);
      setRuns(closeRuns);
      const runFromUrl = readParam('run');
      setSelectedRunId((current) => runFromUrl || current || runId(closeRuns[0]) || '');
    } catch (e) {
      setError(String(e.message || e));
    }
  };

  useEffect(() => {
    loadRuns();
    closeIntelApi
      .listExpectations('company')
      .then((payload) => {
        const byId = {};
        (payload?.rows || []).forEach((row) => {
          if (row.id) byId[row.id] = row;
        });
        setExpectationsByKey(byId);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedRunId) return;
    setError('');
    setBoard(null);
    setBridges({});
    setEvidence({});
    setFocusLineKey('');
    setLoadingBridgeKeys(new Set());
    bridgesRef.current = {};
    inFlightRef.current = new Set();
    writeParam('run', selectedRunId);
    closeIntelApi
      .board(selectedRunId, { basis })
      .then(setBoard)
      .catch((e) => setError(String(e.message || e)));
    closeIntelApi
      .evidence(selectedRunId)
      .then(setEvidence)
      .catch(() => setEvidence({}));
  }, [selectedRunId, basis]);

  // Monitor findings behind this close period — powers the SIGNAL VS NOISE rail.
  useEffect(() => {
    const period = board?.asOfPeriod;
    if (!period) return;
    setFindingsLoading(true);
    closeIntelApi
      .listFindings({ asOfPeriod: period })
      .then((payload) => setFindings(payload?.rows || []))
      .catch(() => setFindings([]))
      .finally(() => setFindingsLoading(false));
  }, [board?.asOfPeriod]);

  const lines = useMemo(() => board?.lines || [], [board]);
  const findingsById = useMemo(() => findingLookup(findings), [findings]);

  const fetchBridge = useCallback(
    async (line) => {
      const key = bridgeKey(line);
      if (!selectedRunId) return;
      if (bridgesRef.current[key] || inFlightRef.current.has(key)) return;
      inFlightRef.current.add(key);
      setLoadingBridgeKeys(new Set(inFlightRef.current));
      try {
        const payload = await closeIntelApi.bridge(selectedRunId, {
          level: line.scopeLevel || 'line',
          entity: line.planningEntity,
          glGroup: line.glGroup,
          basis
        });
        bridgesRef.current = { ...bridgesRef.current, [key]: payload };
        setBridges(bridgesRef.current);
      } catch (e) {
        bridgesRef.current = {
          ...bridgesRef.current,
          [key]: { error: String(e.message || e), components: [] }
        };
        setBridges(bridgesRef.current);
      } finally {
        inFlightRef.current.delete(key);
        setLoadingBridgeKeys(new Set(inFlightRef.current));
      }
    },
    [selectedRunId, basis]
  );

  const selectScreen = (id) => {
    const screen = screenById(id).id;
    setScreenId(screen);
    setReturnToExecutive(false);
    writeParam('screen', screen);
    writeParam('returnTo', '');
  };

  const selectExecutiveItem = useCallback((itemId) => {
    setExecutiveItemId(itemId || '');
    writeParam('execItem', itemId || '');
  }, []);

  const openExecutiveDetail = useCallback((id, itemId) => {
    const target = executiveDetailTarget(id, itemId);
    setScreenId(target.screenId);
    setExecutiveItemId(target.executiveItemId || '');
    setReturnToExecutive(target.returnToExecutive);
    writeParam('screen', target.screenId);
    writeParam('execItem', target.executiveItemId);
    writeParam('returnTo', 'exec');
  }, []);

  const returnToExecutiveReview = useCallback(() => {
    const target = executiveReturnTarget();
    setScreenId(target.screenId);
    setReturnToExecutive(target.returnToExecutive);
    writeParam('screen', target.screenId);
    writeParam('returnTo', '');
  }, []);

  const openWorkbench = useCallback(
    (finding) => {
      if (!finding || !navigateTab) return;
      navigateTab('monitor', {
        finding: finding.id,
        period: String(finding.asOfPeriod || board?.asOfPeriod || '').slice(0, 7)
      });
    },
    [navigateTab, board?.asOfPeriod]
  );

  const onSelectBridge = useCallback(
    (bridgeInfo) => {
      if (!bridgeInfo) return;
      const screen =
        bridgeInfo.screenId ||
        screenForGlGroup(bridgeInfo.glGroup, lines) ||
        'nim';
      setScreenId(screen);
      writeParam('screen', screen);
      if (bridgeInfo.lineKey) setFocusLineKey(bridgeInfo.lineKey);
    },
    [lines]
  );

  const selectExecutiveBridge = useCallback(
    (bridgeInfo, itemId) => {
      if (!bridgeInfo) return;
      const screen =
        bridgeInfo.screenId ||
        screenForGlGroup(bridgeInfo.glGroup, lines) ||
        'nim';
      openExecutiveDetail(screen, itemId || executiveItemId);
      if (bridgeInfo.lineKey) setFocusLineKey(bridgeInfo.lineKey);
    },
    [executiveItemId, lines, openExecutiveDetail]
  );

  return (
    <section className="ace-panel bp-shell" style={{ padding: 18, borderRadius: 16 }}>
      <div className="bp-header">
        <div>
          <div className="ace-eyebrow">CI_05 <span aria-hidden>●</span> Board page</div>
          <h2 style={{ margin: '5px 0 0' }}>Earnings review pack</h2>
        </div>
        <div className="bp-header-controls">
          <select
            className="ace-input ci-tip"
            data-tip="Pick which close run to review"
            value={selectedRunId}
            onChange={(event) => setSelectedRunId(event.target.value)}
            style={{ width: 230 }}
          >
            {runs.map((run) => (
              <option key={runId(run)} value={runId(run)}>
                {(run.asOfPeriod || run.as_of_period || 'period n/a')} · {String(runId(run)).slice(0, 8)}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="ace-pill ci-tip"
            data-tip="Refresh the close-run list"
            onClick={loadRuns}
          >
            <RefreshCw size={13} /> Runs
          </button>
        </div>
      </div>

      <div className="bp-basis-tabs">
        {BASIS_OPTIONS.map((option) => (
          <button
            key={option}
            type="button"
            className={`ace-pill${basis === option ? ' ace-pill--active' : ''} ci-tip`}
            data-tip={`Compare actuals against ${basisTabLabel(option).toLowerCase()}`}
            onClick={() => setBasis(option)}
          >
            {basisTabLabel(option)}
          </button>
        ))}
      </div>

      <nav className="bs-breadcrumbs" aria-label="Board pack screens">
        {SCREEN_DEFS.map((screen) => (
          <button
            key={screen.id}
            type="button"
            className={`bs-crumb ci-tip${screenId === screen.id ? ' bs-crumb--active' : ''}`}
            data-tip={screen.tip}
            aria-current={screenId === screen.id ? 'page' : undefined}
            onClick={() => selectScreen(screen.id)}
          >
            <span className="bs-crumb-ord">{screen.ord}</span> {screen.label}
          </button>
        ))}
      </nav>

      <SectionGuide guideId="board" />

      {error && <p className="bp-negative">{error}</p>}
      {!board && !error && <p className="bp-muted">Loading board pack…</p>}

      {board && screenId === 'exec' && (
        <BoardPackExecutiveSummary
          board={board}
          basis={basis}
          runId={selectedRunId}
          findingsById={findingsById}
          expectationsByKey={expectationsByKey}
          evidence={evidence}
          selectedItemId={executiveItemId}
          onSelectItem={selectExecutiveItem}
          onNavigateScreen={openExecutiveDetail}
          onSelectFinding={openWorkbench}
          onSelectBridge={selectExecutiveBridge}
        />
      )}

      {board && screenId !== 'exec' && (
        <BoardScreen
          screenId={screenId}
          lines={lines}
          execRows={board?.execRows}
          basis={basis}
          bridges={bridges}
          loadingKeys={loadingBridgeKeys}
          fetchBridge={fetchBridge}
          findingsById={findingsById}
          expectationsByKey={expectationsByKey}
          evidence={evidence}
          findings={findings}
          findingsLoading={findingsLoading}
          focusLineKey={focusLineKey}
          onFocusConsumed={() => setFocusLineKey('')}
          onOpenWorkbench={openWorkbench}
          onSelectBridge={onSelectBridge}
          onReturnToExecutive={returnToExecutive ? returnToExecutiveReview : undefined}
        />
      )}
    </section>
  );
}
